"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { InvoiceCard } from "./InvoiceCard";
import { CustomerMappingsCard } from "./CustomerMappingsCard";
import type { InvoiceDisplayItem } from "@/lib/utils/crm/quickbooks-company-ref";

interface InvoiceListProps {
  invoices: InvoiceDisplayItem[];
  displayNamesText: string;
  onViewDetails: (invoiceId: string, realmId?: string) => void;
  getInvoiceCustomerName: (invoiceItem: InvoiceDisplayItem) => string;
  contactId?: string;
  onOpenMatchDrawer?: () => void;
  existingMappings?: Array<{ customerId: string; realmId?: string }>;
  onRemoveMapping?: (customerId: string) => void;
  getDisplayNameForCustomer?: (customerId: string) => string;
  isRemoving?: boolean;
}

export function InvoiceList({
  invoices,
  displayNamesText,
  onViewDetails,
  getInvoiceCustomerName,
  contactId,
  onOpenMatchDrawer,
  existingMappings = [],
  onRemoveMapping,
  getDisplayNameForCustomer,
  isRemoving = false,
}: InvoiceListProps) {
  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Invoices for {displayNamesText}</h3>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {invoices.length} invoice{invoices.length !== 1 ? "s" : ""} found
          </Badge>
          {contactId && onOpenMatchDrawer && (
            <Button variant="outline" size="sm" onClick={onOpenMatchDrawer}>
              <Plus className="h-4 w-4 mr-1" />
              Add QB Customer
            </Button>
          )}
        </div>
      </div>

      {/* Show existing QuickBooks customer mappings */}
      {contactId && existingMappings.length > 0 && getDisplayNameForCustomer && onRemoveMapping && (
        <CustomerMappingsCard
          mappings={existingMappings}
          onRemove={onRemoveMapping}
          getDisplayNameForCustomer={getDisplayNameForCustomer}
          isRemoving={isRemoving}
        />
      )}

      <div className="grid gap-4">
        {invoices.map((item) => (
          <InvoiceCard
            key={item.data.Id}
            invoice={item}
            onViewDetails={onViewDetails}
            getInvoiceCustomerName={getInvoiceCustomerName}
          />
        ))}
      </div>
    </div>
  );
}
